import type { MouseEvent, FC } from 'react'
import { Fragment, useEffect } from 'react'
import { Box, Link, Stack, Typography, type TypographyOwnProps, useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useCookiePreferenceButton, useOneTrustContext } from '@dhl/onetrust'
import { useNativeBridge } from '@dhl/native-bridge'
import { type Links } from './model.js'

import '@dhl/theme' // Needed for extra Palette interface options defined in theme.ts

type Props = {
  /**
   * Whether the content should be displayed in a single line.
   * Set to true to display links or content in a condensed one-liner format.
   */
  singleLine?: boolean

  /**
   * A list of visible links that should be shown in the UI.
   * Can include 'company', 'termsAndConditions', and 'privacy'.
   */
  visibleLinks?: Links[]

  /**
   * Applies the theme typography styles.
   */
  variant?: TypographyOwnProps['variant']
}

type LegalLinkProps = {
  link: Links
  variant?: TypographyOwnProps['variant']
}

export const LegalLink: FC<LegalLinkProps> = ({ link, variant = 'body2' }) => {
  const { t } = useTranslation()

  return (
    <Link variant={variant} color="textPrimary" href={t(`legal.${link}.labelLink`)} rel="noreferrer" target="_blank">
      {t(`legal.${link}.labelText`)}
    </Link>
  )
}

/**
 * @deprecated Use LegalLinks from '@dhl/legal' instead, which supports the new design system.
 */
export const LegalLinksLegacy: FC<Props> = ({
  singleLine,
  visibleLinks = ['company', 'privacy'],
  variant = 'body2',
}) => {
  const { t } = useTranslation()
  const { cookiePreferenceId, onCookiePreferenceButtonClick, cookiePreferenceText, showCookiePreferenceButton } =
    useCookiePreferenceButton()
  const { isOneTrustLoaded } = useOneTrustContext()

  const {
    palette: {
      mode,
      background: { backdrop: backdropColor },
      common: { white },
      grey,
    },
  } = useTheme()

  const { hideNativeBackdrop: hideBackdrop, showNativeBackdrop: showBackdrop } = useNativeBridge()

  useEffect(() => {
    if (!isOneTrustLoaded) {
      return
    }

    window.OneTrust?.OnConsentChanged?.(() => {
      hideBackdrop()
    })
  }, [hideBackdrop, isOneTrustLoaded])

  const handleCookiePreferenceButtonClick = (e: MouseEvent) => {
    showBackdrop({
      color: backdropColor,
      bottomColor: mode === 'light' ? white : grey[100],
      visibleOnFooter: true,
      transition: 'fade',
    })
    onCookiePreferenceButtonClick(e)
  }

  const showVisibleLinks = visibleLinks.length > 0

  return (
    <Stack display={singleLine ? 'inline-flex' : 'flex'} alignItems="center">
      {showVisibleLinks && (
        <Stack
          alignItems="center"
          flexDirection={singleLine ? 'row' : 'column'}
          flexWrap="wrap"
          justifyContent="center"
        >
          {visibleLinks.map((link, index) => (
            <Fragment
              // eslint-disable-next-line react/no-array-index-key
              key={`legal-link-${index}`}
            >
              <LegalLink link={link} variant={variant} />
              {singleLine && index < visibleLinks.length - 2 && (
                <Typography variant={variant} mr={0.5}>
                  ,
                </Typography>
              )}
              {singleLine && visibleLinks.length - (index + 1) === 1 && (
                <Typography variant={variant} m={0.5}>
                  {t('legal.andText')}
                </Typography>
              )}
            </Fragment>
          ))}
        </Stack>
      )}
      {showCookiePreferenceButton && (
        <Box mt={showVisibleLinks ? 3 : 0} textAlign="center">
          <Link
            id={cookiePreferenceId}
            variant={variant}
            color="textPrimary"
            href="#cookie"
            rel="noreferrer"
            target="_blank"
            onClick={handleCookiePreferenceButtonClick}
            whiteSpace="normal"
          >
            {cookiePreferenceText}
          </Link>
        </Box>
      )}
    </Stack>
  )
}
